import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosHeaders
} from 'axios'
import queryString from 'query-string'
import { isJsonString, ErrorHandler } from '~/utils'
import { REFRESH_TOKEN_API } from '~/constants/APIs'
import { setUser } from '~/redux/features'
import { store } from '~/app/store'
import { IAuthState } from '~/models'

const baseUrl = process.env.REACT_APP_API_HOST_URL
export const getToken = (): string | null | void => {
  if (
    !localStorage.getItem('user') ||
    !isJsonString(localStorage.getItem('user') as string)
  ) {
    return null
  }
  return JSON.parse(localStorage.getItem('user') as string)?.token
}

export const getProfilePicture = (): string | null | void => {
  if (!localStorage.getItem('user')) {
    return null
  }
  return JSON.parse(localStorage.getItem('user') as string)?.profilePictureUrl
}

export const getUserName = (): string | null | void => {
  if (!localStorage.getItem('user')) {
    return null
  }
  return JSON.parse(localStorage.getItem('user') as string)?.userName
}

export const getUserInfo = () => {
  if (!localStorage.getItem('user')) {
    return null
  }
  return JSON.parse(localStorage.getItem('user') as string)
}

export const getRefreshToken = (): string | null | void => {
  if (!localStorage.getItem('user')) {
    return null
  }
  return JSON.parse(localStorage.getItem('user') as string)?.refreshToken
}

type CustomAxiosHeaders = AxiosHeaders & {
  Authorization?: string
}

let isRefreshing = false

const axiosClient: AxiosInstance = axios.create({
  baseURL: baseUrl,
  paramsSerializer: params => queryString.stringify(params)
})
axiosClient.defaults.maxRedirects = 0
axiosClient.interceptors.request.use(async (config: AxiosRequestConfig) => {
  const token = getToken()
  if (!config.headers) config.headers = {}
  if (token) config.headers.Authorization = `Bearer ${token}`
  return { ...config, headers: config.headers as unknown as CustomAxiosHeaders }
})

axiosClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const responseType = response.config.responseType

    if (responseType === 'blob') {
      downloadFile(response)
    }
    return response.data ?? response
  },
  async error => {
    const originalConfig = error.config

    if (!error.response) {
      if (error?.code === 'ERR_NETWORK') window.location.reload()
      throw error
    }

    // handle redirect to login page if refresh token and bearer token are exprired
    if (
      // getUserInfo()?.errorCode !== 1 &&
      error.response.status === 400 &&
      error.request.responseURL.split('/').slice(-1)[0] === 'refreshtoken'
    ) {
      ErrorHandler(401)
    }

    if (isRefreshing) {
      await waitForRefreshToken()
      return axiosClient(originalConfig)
    }

    // check if the password is expired
    if (error.response.status === 401 && error.response.data.errorCode === 1) {
      store.dispatch(
        setUser({
          fullName: getUserInfo()?.fullName ?? '',
          userName: getUserName() ?? '',
          refreshToken: getRefreshToken() ?? '',
          roles: getUserInfo()?.roles ?? [],
          token: getUserInfo()?.token ?? '',
          profilePictureUrl: getProfilePicture() ?? undefined,
          errorCode: 1
        })
      )
      throw error
    }

    //handle refresh token
    if (
      error.response.status === 401 &&
      !originalConfig._retry &&
      error.response.data.errorCode !== 1
    ) {
      originalConfig._retry = true
      isRefreshing = true

      try {
        const userName = getUserName()
        const refreshToken = getRefreshToken()

        const rs = (await axiosClient.post(REFRESH_TOKEN_API, {
          userName: userName,
          refreshToken: refreshToken
        })) as unknown as IAuthState

        store.dispatch(
          setUser({ ...rs, profilePictureUrl: getProfilePicture() ?? undefined })
        )

        return axiosClient(originalConfig)
      } catch (err) {
        ErrorHandler(401)
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    if (error.response.data['status'] === 302) {
      window.location.href = error.response?.data['messages'][0]
      return Promise.reject('Redirect')
    }

    ErrorHandler(error.response.status)
    throw error.response
  }
)

const waitForRefreshToken = () => {
  return new Promise(resolve => {
    const interval = setInterval(() => {
      if (!isRefreshing) {
        clearInterval(interval)
        resolve(true)
      }
    }, 100)
  })
}

const parseFilenameFromDisposition = (disposition?: string): string => {
  if (!disposition) return 'file'

  // RFC 5987: filename*=UTF-8''<url-encoded-filename>
  const filenameStarMatch = disposition.match(/filename\*\s*=\s*([^;]+)/i)
  if (filenameStarMatch?.[1]) {
    const rawValue = filenameStarMatch[1].trim().replace(/^['"]|['"]$/g, '')
    const encoded = rawValue.replace(/^UTF-8''/i, '')

    try {
      return decodeURIComponent(encoded)
    } catch {
      return encoded
    }
  }

  // RFC 6266: filename="..." or filename=...
  const filenameMatch = disposition.match(/filename\s*=\s*("([^"]*)"|([^;]+))/i)
  if (filenameMatch) {
    const raw = (filenameMatch[2] ?? filenameMatch[3] ?? '').trim()
    return raw.replace(/^['"]|['"]$/g, '').replace(/\\"/g, '"') || 'file'
  }

  return 'file'
}

// Function to handle file download
const downloadFile = (response: AxiosResponse) => {
  const disposition = response.headers['content-disposition']
  const filename = parseFilenameFromDisposition(disposition)

  const link = document.createElement('a')
  link.href = URL.createObjectURL(response.data)
  link.download = filename

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(link.href)
}

export default axiosClient
